<template>
  <nav class="bg-white shadow-sm border-b">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center py-4">
        <!-- Logo et titre -->
        <div class="flex items-center">
          <router-link to="/" class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <h1 class="text-xl font-bold text-gray-900">{{ $t('home.title') }}</h1>
          </router-link>
        </div>

        <!-- Navigation links -->
        <div class="hidden md:flex items-center space-x-6">
          <router-link to="/" class="text-gray-600 hover:text-indigo-600 font-medium transition-colors">
            {{ $t('nav.home') }}
          </router-link>
          <router-link to="/about" class="text-gray-600 hover:text-indigo-600 font-medium transition-colors">
            {{ $t('nav.about') }}
          </router-link>
          <router-link to="/contact" class="text-gray-600 hover:text-indigo-600 font-medium transition-colors">
            {{ $t('nav.contact') }}
          </router-link>
          <!-- Admin link for admin users -->
          <router-link 
            v-if="currentUser && currentUser.role === 'admin'" 
            to="/admin/dashboard" 
            class="text-gray-600 hover:text-indigo-600 font-medium transition-colors"
          >
            {{ $t('nav.admin') }}
          </router-link>
        </div>

        <!-- User section and language selector -->
        <div class="flex items-center space-x-4">
          <!-- User authentication -->
          <template v-if="currentUser">
            <div class="flex items-center space-x-3">
              <span class="text-sm text-gray-600">
                {{ $t('auth.welcome') }} {{ currentUser.email }}
              </span>
              <button 
                @click="logout" 
                class="text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
              >
                {{ $t('auth.logout') }}
              </button>
            </div>
          </template>
          <template v-else>
            <div class="flex items-center space-x-3">
              <router-link 
                to="/login" 
                class="text-sm text-gray-600 hover:text-indigo-600 font-medium transition-colors"
              >
                {{ $t('auth.login.title') }}
              </router-link>
              <router-link 
                to="/register" 
                class="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 font-medium transition-colors"
              >
                {{ $t('auth.register.title') }}
              </router-link>
            </div>
          </template>

          <!-- Language selector -->
          <div class="border-l border-gray-300 pl-4">
            <LanguageSelector />
          </div>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden">
          <button 
            @click="toggleMobileMenu" 
            class="text-gray-600 hover:text-indigo-600 focus:outline-none focus:text-indigo-600"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="!isMobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="isMobileMenuOpen" class="md:hidden border-t border-gray-200 pt-4 pb-3">
        <div class="space-y-3">
          <router-link 
            to="/" 
            @click="closeMobileMenu"
            class="block text-gray-600 hover:text-indigo-600 font-medium transition-colors"
          >
            {{ $t('nav.home') }}
          </router-link>
          <router-link 
            to="/about" 
            @click="closeMobileMenu"
            class="block text-gray-600 hover:text-indigo-600 font-medium transition-colors"
          >
            {{ $t('nav.about') }}
          </router-link>
          <router-link 
            to="/contact" 
            @click="closeMobileMenu"
            class="block text-gray-600 hover:text-indigo-600 font-medium transition-colors"
          >
            {{ $t('nav.contact') }}
          </router-link>
          <!-- Admin link for admin users (mobile) -->
          <router-link 
            v-if="currentUser && currentUser.role === 'admin'"
            to="/admin/dashboard" 
            @click="closeMobileMenu"
            class="block text-gray-600 hover:text-indigo-600 font-medium transition-colors"
          >
            {{ $t('nav.admin') }}
          </router-link>
          
          <!-- Mobile user section -->
          <div class="border-t border-gray-200 pt-3">
            <template v-if="currentUser">
              <div class="space-y-2">
                <span class="block text-sm text-gray-600">
                  {{ $t('auth.welcome') }} {{ currentUser.email }}
                </span>
                <button 
                  @click="logout" 
                  class="block text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
                >
                  {{ $t('auth.logout') }}
                </button>
              </div>
            </template>
            <template v-else>
              <div class="space-y-2">
                <router-link 
                  to="/login" 
                  @click="closeMobileMenu"
                  class="block text-sm text-gray-600 hover:text-indigo-600 font-medium transition-colors"
                >
                  {{ $t('auth.login.title') }}
                </router-link>
                <router-link 
                  to="/register" 
                  @click="closeMobileMenu"
                  class="block text-sm bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 font-medium transition-colors"
                >
                  {{ $t('auth.register.title') }}
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import LanguageSelector from './LanguageSelector.vue'

export default {
  name: 'NavigationBar',
  components: {
    LanguageSelector
  },
  data() {
    return {
      currentUser: null,
      isMobileMenuOpen: false
    }
  },
  mounted() {
    this.loadCurrentUser()
  },
  methods: {
    loadCurrentUser() {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          this.currentUser = JSON.parse(userStr)
        } catch (error) {
          console.error('Error parsing user data:', error)
          localStorage.removeItem('user')
        }
      }
    },
    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.currentUser = null
      this.closeMobileMenu()
      this.$router.push('/')
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false
    }
  }
}
</script> 